import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { ArrowLeft, X, Sparkles } from 'lucide-react';
import { Portal, WeekNode, TerritoryHeader } from '@/components/journey/JourneyPieces';
import SpiritWeekGuideCard from '@/components/progression/SpiritWeekGuideCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PortalCinematic = ({ phase, onDone }) => {
  useEffect(() => {
    confetti({ particleCount: 220, spread: 120, origin: { y: 0.5 }, colors: [phase.color, '#ffffff', '#a3e635'] });
    const t = setTimeout(onDone, 4200); return () => clearTimeout(t);
  }, [phase, onDone]);
  return (
    <motion.div data-testid="portal-cinematic" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9980] bg-black flex flex-col items-center justify-center text-center p-6">
      <motion.div initial={{ scale: 0.2, opacity: 0 }} animate={{ scale: [0.2, 1.4, 1], opacity: 1 }} transition={{ duration: 1.6, ease: 'easeOut' }}><Portal color={phase.color} open size={220} /></motion.div>
      <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }} className="mt-8 text-[10px] uppercase tracking-[0.5em]" style={{ color: phase.color }}>Portal aberto</motion.p>
      <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6 }} className="text-4xl sm:text-5xl font-black text-white mt-2" style={{ textShadow: `0 0 40px ${phase.color}` }}>{phase.name_pt}</motion.h1>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2 }} className="text-zinc-400 mt-3 max-w-sm">{phase.tagline_pt || 'Um novo território desbloqueado. O Spirit já preparou a tua primeira semana.'}</motion.p>
    </motion.div>
  );
};

export default function JourneyMapPage() {
  const { program } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [cinematic, setCinematic] = useState(null);
  const scrollRef = useRef(null);
  const { scrollYProgress } = useScroll({ container: scrollRef });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);

  const load = async () => {
    try { const r = await axios.get(`${API}/spirit/agent/journey/${program}`, auth()); setData(r.data); return r.data; } catch (_) { setData({ enrolled: false }); }
  };
  useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [program]);

  useEffect(() => {
    const portal = params.get('portal');
    if (portal && data?.phases) {
      const ph = data.phases.find(p => String(p.id) === portal);
      const t = setTimeout(() => { if (ph) setCinematic(ph); params.delete('portal'); setParams(params, { replace: true }); }, 0);
      return () => clearTimeout(t);
    }
  }, [data, params, setParams]);

  useEffect(() => {
    if (!data?.weeks || !scrollRef.current) return;
    const t = setTimeout(() => document.querySelector('[data-testid="journey-player"]')?.closest('button')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 500);
    return () => clearTimeout(t);
  }, [data]);

  const onWeekClosed = async (res) => {
    setSelected(null);
    const d = await load();
    if (res?.phase_advanced && d?.phases) {
      const next = d.phases.find(p => p.id === res.next_phase_id);
      if (next) setCinematic(next);
    }
  };

  if (!data) return <div className="min-h-screen bg-black" />;
  if (!data.enrolled) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-8 text-center" data-testid="journey-not-enrolled">
      <Sparkles className="w-8 h-8 text-lime-400 mb-4" />
      <p className="text-zinc-300">Ainda não começaste este programa.</p>
      <button onClick={() => navigate(-1)} className="mt-6 text-sm text-lime-300">Voltar</button>
    </div>
  );

  const current = data.weeks.find(w => w.status === 'active');

  return (
    <div ref={scrollRef} className="fixed inset-0 overflow-y-auto bg-black text-white" data-testid="journey-map">
      <motion.div style={{ y: bgY }} className="pointer-events-none fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:56px_56px]" />
      {data.phases.map((p, i) => (
        <div key={p.id} className="pointer-events-none fixed inset-x-0 h-[25vh] opacity-40 blur-3xl" style={{ top: `${i * 25}vh`, background: `radial-gradient(ellipse at ${i % 2 ? '80%' : '20%'} 50%, ${p.color}55, transparent 70%)` }} />
      ))}

      <header className="sticky top-0 z-20 backdrop-blur-xl bg-black/60 border-b border-white/5 px-4 py-3 flex items-center justify-between">
        <button data-testid="journey-back" onClick={() => navigate(-1)} className="w-9 h-9 rounded-full border border-zinc-800 flex items-center justify-center text-zinc-300"><ArrowLeft className="w-4 h-4" /></button>
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-[0.4em] text-zinc-500">Mapa da Jornada</p>
          <p className="text-sm font-black" data-testid="journey-title">{data.program_name_pt} · Semana {data.current_week}/{data.weeks.length}</p>
        </div>
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-black text-black" style={{ background: current?.phase.color || '#a3e635' }} data-testid="journey-progress">{data.progress_pct}%</div>
      </header>

      <main className="relative max-w-3xl mx-auto px-3 pb-40 pt-6">
        <svg className="absolute left-1/2 -translate-x-1/2 top-0 h-full w-24 pointer-events-none" preserveAspectRatio="none" viewBox="0 0 100 1000">
          <motion.path d="M50 0 C 90 120, 10 220, 50 340 S 90 560, 50 680 S 10 900, 50 1000" fill="none" stroke="url(#g)" strokeWidth="2" strokeDasharray="6 8"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 2.5, ease: 'easeInOut' }} />
          <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1">{data.phases.map((p, i) => <stop key={p.id} offset={`${(i / Math.max(1, data.phases.length - 1)) * 100}%`} stopColor={p.color} />)}</linearGradient></defs>
        </svg>

        <div className="relative flex flex-col gap-5">
          {data.phases.map((ph, pi) => {
            const weeks = data.weeks.filter(w => w.phase.id === ph.id);
            const gateWeek = weeks[weeks.length - 1];
            return (
              <React.Fragment key={ph.id}>
                <TerritoryHeader phase={ph} index={pi} />
                {weeks.map((w, wi) => <WeekNode key={w.week} w={w} side={(pi * 3 + wi) % 2 ? 'right' : 'left'} index={wi} onClick={setSelected} />)}
                <div className="self-center my-4"><Portal color={ph.color} open={ph.passed} label={ph.passed ? 'Atravessado' : gateWeek?.status === 'active' ? 'Fecha a semana para abrir' : 'Selado'} /></div>
              </React.Fragment>
            );
          })}
          {data.program_completed && <p className="text-center text-lime-300 font-black text-xl mt-6" data-testid="journey-complete">🏆 Jornada completa</p>}
        </div>
      </main>

      <AnimatePresence>
        {selected && (
          <motion.div data-testid="journey-week-drawer" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-30 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={() => setSelected(null)}>
            <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} transition={{ type: 'spring', stiffness: 260, damping: 26 }} onClick={e => e.stopPropagation()}
              className="w-full sm:max-w-xl max-h-[88vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border bg-zinc-950 p-5" style={{ borderColor: `${selected.phase.color}55` }}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em]" style={{ color: selected.phase.color }}>{selected.phase.name_pt} · Semana {selected.week}</p>
                  <h3 className="text-xl font-black">{selected.emoji} {selected.title_pt}</h3>
                </div>
                <button data-testid="journey-drawer-close" onClick={() => setSelected(null)} className="w-9 h-9 rounded-full border border-zinc-800 flex items-center justify-center text-zinc-400"><X className="w-4 h-4" /></button>
              </div>
              {selected.status === 'locked' ? (
                <div data-testid="journey-locked-msg" className="rounded-xl border border-zinc-800 p-4 text-sm text-zinc-400">🔒 Território selado. Fecha a semana {data.current_week} para o Spirit te guiar até aqui.<p className="mt-2 text-xs text-zinc-600 blur-[1px] select-none">{selected.focus_pt}</p></div>
              ) : (
                <>
                  <p className="text-sm text-zinc-300 leading-relaxed">{selected.focus_pt}</p>
                  <ol className="mt-3 space-y-1.5">{selected.orders_pt.map((o, i) => <li key={i} className="text-xs text-zinc-300 flex gap-2"><span className="font-black" style={{ color: selected.phase.color }}>{i + 1}.</span>{o}</li>)}</ol>
                  {selected.learn_pt && <p className="mt-3 text-xs text-zinc-500"><span className="text-zinc-400 font-semibold">Aprender:</span> {selected.learn_pt}</p>}
                  {selected.status === 'active' && <div className="mt-5"><SpiritWeekGuideCard program={program} onWeekClosed={onWeekClosed} /></div>}
                  {selected.status === 'closed' && <p className="mt-4 text-xs text-lime-300">✓ Semana fechada. Bem jogado.</p>}
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>{cinematic && <PortalCinematic phase={cinematic} onDone={() => setCinematic(null)} />}</AnimatePresence>
    </div>
  );
}
